/** `tour_template` payload + the preview's `meta` -> the `Tour` the My Bookings list draws.
 *
 * Upstream the list's `Tour` is built per booking (`TourTemplateMapper.TourItemRelation
 * .toDomain`): name, description and cover come from the TEMPLATE (this payload); the dates
 * come from the booking's `start_datetime` plus the template's `length`
 * (`TourDatesMapper.mapDates`); `myTripsDisplay` from the booking's tour item. A template
 * preview has no booking, so:
 *
 * - `myTripsDisplay` is the tour's own "Show bookings in My Trips" switch (`meta`) — off,
 *   the backend never publishes the trip to the app at all (`publishable_to_user`);
 * - the start is a fixed-date tour's `start_date` (`meta`; every booking shares it), else
 *   today — both at local midnight, the template view's convention (`getDatesRange`).
 */
import { toDomainImage, type DomainImage } from '../images';

export interface MyTripsTour {
  id: number;
  name: string;
  description: string | null;
  coverImage: DomainImage | null;
  lengthSeconds: number;
  hasDepartures: boolean;
  /** Shown in My Bookings at all. */
  myTripsDisplay: boolean;
  /** A fixed-date tour's start date (YYYY-MM-DD), else null (an example booking: today). */
  startDate: string | null;
}

const str = (v: unknown): string | null => (typeof v === 'string' && v.length > 0 ? v : null);

export function adaptMyTrips(payload: Record<string, unknown>, meta: Record<string, unknown> = {}): MyTripsTour {
  const tour = (payload.tour ?? {}) as Record<string, unknown>;
  return {
    id: Number(tour.id ?? payload.id ?? 0),
    name: str(tour.name) ?? '',
    description: str(tour.description),
    coverImage: toDomainImage(tour.cover_image),
    lengthSeconds: typeof tour.length === 'number' ? tour.length : 0,
    hasDepartures: tour.has_departures === true,
    myTripsDisplay: meta.mytrips_display !== false,
    startDate: str(meta.start_date),
  };
}
